import { Building2, Radio } from "lucide-react";
import { useSeo } from "../lib/seo";
import { SERVICES, VERTICALS, WHY } from "../data/content";
import { Btn, Container, SectionHead, CheckList } from "../components/site/Ui";
import { MaskLines, Reveal } from "../components/site/Motion";
import { Marquee, CtaBand, DownloadBand } from "../components/site/Blocks";
import { HeroVisual } from "../components/site/HeroVisual";
import { ServiceCard, VerticalCard } from "../components/site/Cards";

const Hero = () => (
  <section data-testid="home-hero" className="relative overflow-hidden bg-white">
    <div className="grid-bg pointer-events-none absolute inset-0" />
    <div className="pointer-events-none absolute -right-32 top-10 h-[560px] w-[560px] rounded-full bg-[#2878E5]/15 blur-[130px]" />
    <div className="pointer-events-none absolute -left-40 bottom-0 h-80 w-80 rounded-full bg-[#D9A93F]/10 blur-[110px]" />
    <Container className="relative grid items-center gap-12 pb-16 pt-12 sm:pt-20 lg:grid-cols-12 lg:pb-24">
      <div className="lg:col-span-7">
        <Reveal><span className="eyebrow">Affiliate & Performance Marketing</span></Reveal>
        <h1 className="mt-6 font-display text-[40px] font-extrabold leading-[1.04] tracking-tight text-[#102B50] sm:text-6xl lg:text-[54px] xl:text-[62px]">
          <MaskLines lines={["Performance Marketing", "That Moves Businesses", "Forward."]} accentIndex={2} />
        </h1>
        <Reveal delay={0.45}>
          <p className="mt-7 max-w-xl text-base leading-relaxed text-[#4A5D78] md:text-lg">
            We connect advertisers with quality traffic partners through innovative affiliate marketing and performance-driven advertising solutions.
          </p>
        </Reveal>
        <Reveal delay={0.55} className="mt-9 flex flex-col gap-3 sm:flex-row">
          <Btn to="/advertisers" data-testid="hero-advertise-btn">Advertise With Us</Btn>
          <Btn to="/publishers" variant="outline" data-testid="hero-publisher-btn">Become a Publisher</Btn>
        </Reveal>
      </div>
      <div className="lg:col-span-5"><HeroVisual /></div>
    </Container>
  </section>
);

const About = () => (
  <section data-testid="home-about" className="bg-white py-20 lg:py-32">
    <Container className="grid items-center gap-14 lg:grid-cols-2">
      <div>
        <SectionHead eyebrow="Who we are" title="Connecting Brands, Partners & Performance." />
        <Reveal delay={0.1} className="mt-6 space-y-4 text-base leading-relaxed text-[#4A5D78] md:text-lg">
          <p>AppTarget sits between advertisers who want measurable growth and publishers who want to monetise their traffic responsibly.</p>
          <p>Advertisers set the outcome they value (an install, a sign-up, a lead or a sale) and we match their campaigns with relevant traffic partners. Publishers earn on the results they deliver, and every party works from clear terms and shared goals.</p>
        </Reveal>
        <Reveal delay={0.2} className="mt-8"><Btn to="/about" variant="navy" data-testid="home-about-btn">More About AppTarget</Btn></Reveal>
      </div>
      <Reveal delay={0.1} className="relative">
        <div className="relative overflow-hidden rounded-[28px] [clip-path:polygon(0_0,100%_0,100%_86%,86%_100%,0_100%)]">
          <img src="/img/office.webp" alt="Marketing team reviewing campaign plans" loading="lazy" width="1400" height="933" className="aspect-[4/3] w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-tr from-[#102B50]/70 via-[#102B50]/10 to-transparent" />
        </div>
        <div className="absolute -bottom-6 left-4 right-4 grid grid-cols-3 gap-2 rounded-2xl border border-slate-200 bg-white p-3 shadow-[0_20px_50px_rgba(16,43,80,0.14)] sm:left-8 sm:right-auto sm:w-[380px]">
          {["Advertiser", "AppTarget", "Publisher"].map((t, i) => (
            <div key={t} className={`rounded-xl px-2 py-3 text-center text-xs font-bold ${i === 1 ? "bg-[#102B50] text-white" : "bg-[#F4F7FB] text-[#102B50]"}`}>{t}</div>
          ))}
        </div>
      </Reveal>
    </Container>
  </section>
);

const Services = () => (
  <section data-testid="home-services" className="bg-[#F4F7FB] py-20 lg:py-32">
    <Container>
      <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <SectionHead eyebrow="Solutions" title="Performance services built around outcomes." text="Eight ways we help advertisers grow and publishers monetise, each tied to measurable results." />
        <Reveal><Btn to="/solutions" variant="outline" data-testid="home-all-solutions-btn">All Solutions</Btn></Reveal>
      </div>
      <div className="mt-14 grid auto-rows-[minmax(240px,auto)] grid-flow-dense gap-5 md:grid-cols-2 lg:grid-cols-4">
        {SERVICES.map((s, i) => <ServiceCard key={s.slug} s={s} i={i} featured={i === 0} wide={i === 7} />)}
      </div>
    </Container>
  </section>
);

const PARTNER = [
  { icon: Building2, tone: "light", eyebrow: "For Advertisers", title: "Acquire customers. Pay for outcomes.", text: "Reach new customers through a curated network of traffic partners and pay on the results that matter to your business.", points: ["Customer acquisition across channels and geos", "Campaign growth with structured scaling", "Lead generation matched to your criteria", "Performance-based CPI, CPA and CPL models"], cta: ["Advertise With Us", "/advertisers"], testid: "partner-advertisers" },
  { icon: Radio, tone: "dark", eyebrow: "For Publishers", title: "Monetise traffic. Grow together.", text: "Turn your audience into reliable revenue with relevant campaigns, clear payout terms and a partnership team that responds.", points: ["Traffic monetisation across verticals", "Access to affiliate campaign opportunities", "Long-term publisher partnerships", "Support to help your business grow"], cta: ["Become a Publisher", "/publishers"], testid: "partner-publishers" },
];

const Partnership = () => (
  <section data-testid="home-partnership" className="bg-white py-20 lg:py-32">
    <Container>
      <SectionHead eyebrow="Partnerships" title="Two sides. One performance ecosystem." center />
      <div className="mt-14 grid gap-6 lg:grid-cols-2">
        {PARTNER.map((p, i) => {
          const dark = p.tone === "dark";
          return (
            <Reveal key={p.title} delay={i * 0.1} data-testid={p.testid}
              className={`relative overflow-hidden rounded-[28px] p-8 sm:p-12 ${dark ? "bg-[#102B50] text-white" : "border border-slate-200 bg-[#F4F7FB]"}`}>
              {dark && <div className="pointer-events-none absolute -bottom-24 -right-24 h-80 w-80 rounded-full bg-[#2878E5]/35 blur-[100px]" />}
              <span className={`relative inline-flex h-14 w-14 items-center justify-center rounded-2xl ${dark ? "bg-white/10 text-white" : "bg-white text-[#2878E5] shadow-sm"}`}><p.icon className="h-6 w-6" /></span>
              <p className={`relative mt-8 text-xs font-bold uppercase tracking-[0.2em] ${dark ? "text-[#8EC0FF]" : "text-[#2878E5]"}`}>{p.eyebrow}</p>
              <h3 className={`relative mt-3 font-display text-3xl font-bold tracking-tight ${dark ? "text-white" : "text-[#102B50]"}`}>{p.title}</h3>
              <p className={`relative mt-4 leading-relaxed ${dark ? "text-slate-300" : "text-[#4A5D78]"}`}>{p.text}</p>
              <div className="relative mt-8"><CheckList items={p.points} light={dark} /></div>
              <div className="relative mt-10"><Btn to={p.cta[1]} variant={dark ? "light" : "primary"}>{p.cta[0]}</Btn></div>
            </Reveal>
          );
        })}
      </div>
    </Container>
  </section>
);

const Why = () => (
  <section data-testid="home-why" className="bg-[#F4F7FB] py-20 lg:py-32">
    <Container className="grid gap-14 lg:grid-cols-12">
      <div className="lg:col-span-4">
        <div className="lg:sticky lg:top-28">
          <SectionHead eyebrow="Why AppTarget" title="A partner built for performance." text="How we work with advertisers and publishers, every day." />
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:col-span-8">
        {WHY.map((w, i) => (
          <Reveal key={w.title} delay={(i % 2) * 0.08} className="card-soft group p-7" data-testid={`why-item-${i}`}>
            <div className="flex items-center justify-between">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#E8F1FD] text-[#2878E5] transition-transform duration-300 group-hover:-rotate-6"><w.icon className="h-5 w-5" /></span>
              <span className="font-display text-sm font-bold text-slate-300">0{i + 1}</span>
            </div>
            <h3 className="mt-6 font-display text-lg font-bold text-[#102B50]">{w.title}</h3>
            <p className="mt-2 text-[15px] leading-relaxed text-[#4A5D78]">{w.text}</p>
          </Reveal>
        ))}
      </div>
    </Container>
  </section>
);

const Verticals = () => (
  <section data-testid="home-verticals" className="bg-white py-20 lg:py-32">
    <Container>
      <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <SectionHead eyebrow="Verticals" title="Industries we work across." text="Campaign experience shaped around the way each industry acquires and keeps customers." />
        <Reveal><Btn to="/verticals" variant="outline" data-testid="home-all-verticals-btn">Explore Verticals</Btn></Reveal>
      </div>
      <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {VERTICALS.map((v, i) => <VerticalCard key={v.slug} v={v} i={i} />)}
      </div>
    </Container>
  </section>
);

export default function Home() {
  useSeo("Affiliate & Performance Marketing Company", "AppTarget connects advertisers with quality traffic partners through affiliate marketing and performance-driven advertising: CPI, CPA, CPL, lead generation and user acquisition.", "/");
  return (
    <>
      <Hero />
      <Marquee />
      <About />
      <Services />
      <Partnership />
      <Why />
      <Verticals />
      <DownloadBand />
      <CtaBand />
    </>
  );
}
